import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { HalfStarRating } from "./HalfStarRating";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "../i18n";

const API = '/api';

export function AddToLibraryDialog({ open, onOpenChange, item, onSuccess, existingEntry }) {
  const { t } = useTranslation();
  const statusOptions = [
    { value: "planned", label: t.statuses.planned },
    { value: "in_progress", label: t.statuses.in_progress },
    { value: "completed", label: t.statuses.completed },
    { value: "dropped", label: t.statuses.dropped },
  ];

  const [status, setStatus] = useState(existingEntry?.status || "planned");
  const [rating, setRating] = useState(existingEntry?.rating || 0);
  const [review, setReview] = useState(existingEntry?.review || "");
  const [notes, setNotes] = useState(existingEntry?.notes || "");
  const [priority, setPriority] = useState(existingEntry?.priority || "");
  const [loading, setLoading] = useState(false);

  const isEditing = !!existingEntry;
  const canRate = status === "completed" || status === "dropped";
  const showPriority = status === "planned";

  // Reset rating/review when switching to a non-ratable status
  useEffect(() => {
    if (!canRate) {
      setRating(0);
      setReview("");
    }
  }, [canRate]);

  // Sync state from existingEntry when dialog opens
  useEffect(() => {
    if (open && existingEntry) {
      setStatus(existingEntry.status || "planned");
      setRating(existingEntry.rating || 0);
      setReview(existingEntry.review || "");
      setNotes(existingEntry.notes || "");
      setPriority(existingEntry.priority || "");
    } else if (open && !existingEntry) {
      setStatus("planned");
      setRating(0);
      setReview("");
      setNotes("");
      setPriority("");
    }
  }, [open, existingEntry]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        status,
        rating: canRate && rating ? rating : null,
        review: canRate && review ? review : null,
        notes: notes || null,
      };

      if (showPriority && priority) {
        payload.priority = parseInt(priority);
      }

      if (isEditing) {
        await axios.put(`${API}/library/${existingEntry.entry_id}`, payload);
        toast.success(t.addToLibraryDialog.updated);
      } else {
        await axios.post(`${API}/library`, { item_id: item.item_id, ...payload });
        toast.success(t.addToLibraryDialog.added);
      }

      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.detail || t.addToLibraryDialog.saveError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="add-to-library-dialog">
        <DialogHeader>
          <DialogTitle>{isEditing ? t.addToLibraryDialog.editLibrary : t.addToLibraryDialog.addToLibrary}</DialogTitle>
          <DialogDescription>{item?.title}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Status */}
          <div className="space-y-2">
            <Label>{t.addToLibraryDialog.status}</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Priority (only for planned) */}
          {showPriority && (
            <div className="space-y-2" data-testid="priority-field">
              <Label>{t.library.priorityLabel} (1-999)</Label>
              <Input
                type="number"
                min={1}
                max={999}
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                placeholder="Auto"
                data-testid="priority-input"
              />
            </div>
          )}

          {/* Rating (only for completed/dropped) */}
          {canRate && (
            <div className="space-y-2" data-testid="rating-field">
              <Label>{t.addToLibraryDialog.rating}</Label>
              <HalfStarRating value={rating} onChange={setRating} size="lg" />
            </div>
          )}

          {/* Review (only for completed/dropped) */}
          {canRate && (
            <div className="space-y-2" data-testid="review-field">
              <Label htmlFor="review">{t.addToLibraryDialog.reviewOptional}</Label>
              <Textarea
                id="review"
                placeholder={t.addToLibraryDialog.reviewPlaceholder}
                value={review}
                onChange={(e) => setReview(e.target.value)}
                rows={3}
                data-testid="review-textarea"
              />
            </div>
          )}

          {/* Notes (always visible) */}
          <div className="space-y-2">
            <Label htmlFor="notes">{t.addToLibraryDialog.notesOptional}</Label>
            <Textarea
              id="notes"
              placeholder={t.addToLibraryDialog.notesPlaceholder}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              data-testid="notes-textarea"
            />
          </div>

          <div className="flex gap-3 justify-end">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t.common.cancel}
            </Button>
            <Button type="submit" disabled={loading} className="btn-scale" data-testid="save-library-entry">
              {loading ? t.common.saving : isEditing ? t.addToLibraryDialog.update : t.addToLibraryDialog.addButton}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
